import { useEffect, useId, useRef, useState } from 'react';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import LightbulbOutlined from '@mui/icons-material/LightbulbOutlined';
import {
  Box,
  Button,
  ButtonBase,
  IconButton,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import { JotCharacter } from '../components/JotCharacter';

/** Presentation inputs only. The future API adapter owns generated Pulse contracts. */
export interface JotPulseProps {
  prompt?: string;
  articleTitle: string;
  /** Change this to show a new suggestion or replay a Storybook presentation. */
  presentationKey: string;
  /** Absolute browser time in milliseconds, supplied by the future adapter. */
  expiresAt?: number;
  reducedMotion?: boolean;
  side?: 'left' | 'right';
  onOpenGuide: () => void;
  onDismiss: () => void;
  onSupport: () => void;
  dismissible?: boolean;
  onShown?: () => void;
  onExpire?: () => void;
}

export function JotPulseBubble({
  prompt,
  articleTitle,
  expiresAt,
  reducedMotion,
  side,
  onOpenGuide,
  onDismiss,
  launcher,
  suppressed = false,
  dismissible = true,
  onShown,
  onExpire,
  bottomOffset,
}: Omit<JotPulseProps, 'presentationKey' | 'onSupport'> & {
  launcher: React.RefObject<HTMLButtonElement | null>;
  suppressed?: boolean;
  bottomOffset?: string;
}) {
  const [hidden, setHidden] = useState(false);
  const acted = useRef(false);
  const bubble = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const expired =
    expiresAt !== undefined && (!Number.isFinite(expiresAt) || expiresAt <= Date.now());
  const mobile = useMediaQuery(useTheme().breakpoints.down('sm'));
  const visible = Boolean(prompt) && !hidden && !expired && !suppressed && !mobile;
  const shown = useRef(false);

  useEffect(() => {
    if (visible && !shown.current) {
      shown.current = true;
      onShown?.();
    }
  }, [visible, onShown]);

  useEffect(() => {
    if (expiresAt === undefined) return;
    const timeout = window.setTimeout(
      () => {
        if (bubble.current?.contains(document.activeElement)) launcher.current?.focus();
        setHidden(true);
        onExpire?.();
      },
      Math.max(0, expiresAt - Date.now()),
    );
    return () => window.clearTimeout(timeout);
  }, [expiresAt, launcher, onExpire]);

  function close(callback: () => void, restoreFocus: boolean) {
    if (acted.current) return;
    acted.current = true;
    if (restoreFocus && bubble.current?.contains(document.activeElement)) launcher.current?.focus();
    setHidden(true);
    callback();
  }

  if (!visible) return null;

  return (
    <Paper
      ref={bubble}
      data-jot-pulse
      role="region"
      aria-label="A suggestion from Jot"
      elevation={0}
      onKeyDown={event => {
        if (dismissible && event.key === 'Escape') {
          event.stopPropagation();
          close(onDismiss, true);
        }
      }}
      sx={{
        position: 'fixed',
        bottom: bottomOffset ?? 'calc(124px + env(safe-area-inset-bottom, 0px))',
        [side ?? 'left']: { xs: '16px', sm: '24px' },
        width: 324,
        maxWidth: 'calc(100vw - 32px)',
        maxHeight: 'calc(100dvh - 148px)',
        zIndex: theme => theme.zIndex.modal - 1,
        p: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '14px',
        boxShadow: '0 8px 36px rgba(28, 30, 85, 0.10), 0 2px 6px rgba(28, 30, 85, 0.04)',
        transformOrigin: side === 'right' ? 'calc(100% - 36px) bottom' : '36px bottom',
        animation: reducedMotion
          ? 'none'
          : 'jotPulseArrive 240ms cubic-bezier(0.2, 0.7, 0.3, 1) both',
        '@keyframes jotPulseArrive': {
          from: { opacity: 0, transform: 'translateY(6px) scale(0.97)' },
          to: { opacity: 1, transform: 'translateY(0) scale(1)' },
        },
        '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: -7,
          [side ?? 'left']: 30,
          width: 13,
          height: 13,
          bgcolor: 'background.paper',
          borderRight: '1px solid',
          borderBottom: '1px solid',
          borderColor: 'divider',
          transform: 'rotate(45deg)',
        },
      }}
    >
      <Box sx={{ maxHeight: 'calc(100dvh - 190px)', overflowY: 'auto' }}>
        {dismissible && (
          <IconButton
            aria-label="Dismiss suggestion"
            onClick={() => close(onDismiss, true)}
            size="small"
            sx={{
              position: 'absolute',
              top: 4,
              right: 4,
              color: 'text.secondary',
              width: 32,
              height: 32,
            }}
          >
            <CloseRounded sx={{ fontSize: 18 }} />
          </IconButton>
        )}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <Box
            sx={{
              width: 24,
              height: 24,
              flexShrink: 0,
              display: 'grid',
              placeItems: 'center',
              borderRadius: '50%',
              bgcolor: 'action.selected',
              color: 'primary.main',
            }}
          >
            <LightbulbOutlined titleAccess="Pulse" sx={{ fontSize: 18 }} />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              id={titleId}
              role="status"
              sx={{ pr: 3, fontSize: 14, lineHeight: 1.4, fontWeight: 500 }}
            >
              {prompt}
            </Typography>
            <Button
              variant="text"
              aria-label={`Read the guide: ${articleTitle}`}
              aria-describedby={titleId}
              onClick={() => close(onOpenGuide, false)}
              endIcon={<ArrowForwardRounded sx={{ fontSize: '16px !important' }} />}
              sx={{
                mt: 0.5,
                ml: -1,
                fontSize: 14,
                fontWeight: 600,
                minHeight: 30,
                py: 0.25,
                textTransform: 'none',
              }}
            >
              Show me how
            </Button>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
}

/** Suggestions are desktop-only (sm and above); mobile keeps Jot available for support. */
export function JotPulse({ side = 'left', ...props }: JotPulseProps) {
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('sm'));
  const launcher = useRef<HTMLButtonElement>(null);

  return (
    <>
      <JotPulseBubble
        key={props.presentationKey}
        {...props}
        side={side}
        launcher={launcher}
        suppressed={mobile}
      />
      <ButtonBase
        ref={launcher}
        data-jot-launcher
        aria-label="Contact support"
        title="Contact support"
        onClick={props.onSupport}
        sx={{
          position: 'fixed',
          [side]: '14px',
          bottom: 'calc(10px + env(safe-area-inset-bottom, 0px))',
          width: mobile ? 80 : 104,
          height: mobile ? 86 : 112,
          borderRadius: 3,
          zIndex: theme.zIndex.snackbar,
          '&.Mui-focusVisible': {
            outline: '2px solid',
            outlineColor: 'primary.main',
            outlineOffset: -8,
          },
        }}
      >
        <JotCharacter
          mood="idle"
          size={mobile ? 80 : 104}
          height={mobile ? 86 : 112}
          reducedMotion={props.reducedMotion}
        />
      </ButtonBase>
    </>
  );
}
